import { useState } from 'preact/hooks';
import {
  platformAuth,
  setAdminTokens,
  PlatformUserSummary,
  AdminAPIError,
} from './api';

type Step =
  | { kind: 'login' }
  | { kind: 'enroll'; enrollmentToken: string; qrBase64: string; otpauthUrl: string; backupCodes: string[] }
  | { kind: 'verify'; challengeToken: string };

interface Props {
  onLoggedIn(user: PlatformUserSummary, devBypassed: boolean): void;
}

export function LoginFlow({ onLoggedIn }: Props) {
  const [step, setStep] = useState<Step>({ kind: 'login' });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmitLogin(e: Event) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    const usernameOrEmail = String(data.get('username') || '').trim();
    const password = String(data.get('password') || '');
    try {
      const resp = await platformAuth.login(usernameOrEmail, password);
      if (resp.enrollmentRequired) {
        setStep({
          kind: 'enroll',
          enrollmentToken: resp.enrollmentRequired.enrollmentToken,
          qrBase64: resp.enrollmentRequired.qrCodePngBase64,
          otpauthUrl: resp.enrollmentRequired.otpauthUrl,
          backupCodes: resp.enrollmentRequired.backupCodes,
        });
      } else if (resp.twoFactorRequired) {
        setStep({ kind: 'verify', challengeToken: resp.twoFactorRequired.challengeToken });
      } else if (resp.tokensIssued) {
        setAdminTokens(resp.tokensIssued.tokens.accessToken, resp.tokensIssued.tokens.refreshToken);
        onLoggedIn(resp.tokensIssued.user, Boolean(resp.tokensIssued.dev_2FaBypassed));
      } else {
        setError('Unexpected server response');
      }
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmitEnroll(e: Event) {
    e.preventDefault();
    if (step.kind !== 'enroll') return;
    setError(null);
    setSubmitting(true);
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    const totp = String(data.get('totp') || '').trim();
    try {
      const resp = await platformAuth.enroll2FA(step.enrollmentToken, totp);
      setAdminTokens(resp.tokens.accessToken, resp.tokens.refreshToken);
      onLoggedIn(resp.user, false);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmitVerify(e: Event) {
    e.preventDefault();
    if (step.kind !== 'verify') return;
    setError(null);
    setSubmitting(true);
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    const code = String(data.get('code') || '').trim();
    try {
      const resp = await platformAuth.verify2FA(step.challengeToken, code);
      setAdminTokens(resp.tokens.accessToken, resp.tokens.refreshToken);
      onLoggedIn(resp.user, false);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div class="admin-auth-shell">
      <div class="admin-auth-card">
        <h1>MomentPix · Platform Admin</h1>
        <p class="hint">
          This surface is for platform staff only. Photographer and
          consumer accounts are not valid here.
        </p>
        {error && <div class="error-banner">{error}</div>}

        {step.kind === 'login' && (
          <form onSubmit={onSubmitLogin}>
            <div class="field">
              <label for="username">Username or email</label>
              <input id="username" name="username" autoFocus required />
            </div>
            <div class="field">
              <label for="password">Password</label>
              <input id="password" name="password" type="password" required />
            </div>
            <button class="primary" type="submit" disabled={submitting} style={{ width: '100%' }}>
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        )}

        {step.kind === 'enroll' && (
          <form onSubmit={onSubmitEnroll}>
            <p>Scan this QR code with your authenticator app:</p>
            <div style={{ textAlign: 'center', margin: '16px 0' }}>
              <img
                src={`data:image/png;base64,${step.qrBase64}`}
                alt="TOTP QR"
                style={{ width: 224, height: 224, background: '#fff', padding: 8, borderRadius: 8 }}
              />
            </div>
            <details style={{ marginBottom: 16 }}>
              <summary style={{ cursor: 'pointer', color: 'var(--admin-text-dim)' }}>
                Can't scan? Show manual entry
              </summary>
              <pre style={{ marginTop: 8 }}>{step.otpauthUrl}</pre>
            </details>
            <div class="warning-banner">
              <strong>Save these backup codes now.</strong> They are shown once. Each can be used
              to sign in one time if you lose access to your authenticator.
              <pre style={{ margin: '12px 0 0 0' }}>{step.backupCodes.join('\n')}</pre>
            </div>
            <div class="field">
              <label for="totp">Enter the 6-digit code from your app</label>
              <input id="totp" name="totp" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required autoFocus />
            </div>
            <button class="primary" type="submit" disabled={submitting} style={{ width: '100%' }}>
              {submitting ? 'Verifying…' : 'Finish enrollment'}
            </button>
          </form>
        )}

        {step.kind === 'verify' && (
          <form onSubmit={onSubmitVerify}>
            <p>Enter the 6-digit code from your authenticator app, or an 8-character backup code.</p>
            <div class="field">
              <label for="code">Code</label>
              <input id="code" name="code" autoFocus required autoComplete="one-time-code" />
            </div>
            <button class="primary" type="submit" disabled={submitting} style={{ width: '100%' }}>
              {submitting ? 'Verifying…' : 'Verify'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function describeError(err: unknown): string {
  if (err instanceof AdminAPIError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Unknown error';
}
